// client/src/components/marketer/project/modals/PerRewardModal.js
import React, { useEffect, useState } from 'react';
import { API_URL } from '../constants';
import AlertModal from './AlertModal';
import '../../../../styles/marketer/modals/PerRewardModal.css';

export default function PerRewardModal({ open, onClose, projectId, initialPerReward, headcount = 0, onSaved }) {
  const [step, setStep] = useState(0);
  const [use, setUse] = useState(false);
  const [pb, setPb] = useState('');   // 구매고객당 리워드
  const [bdg, setBdg] = useState(''); // 총 예산
  const [cnt, setCnt] = useState(''); // 결산 횟수
  const [agree, setAgree] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alertMsg, setAlertMsg] = useState('');

  useEffect(() => {
    if (open) {
      setStep(0);
      setUse(!!initialPerReward?.use);
      setPb(initialPerReward?.rewardPerBuyer ?? '');
      setBdg(initialPerReward?.budgetTotal ?? '');
      setCnt(initialPerReward?.settlementCount ?? '');
      setAgree(false);
      setSaving(false);
    }
  }, [open, initialPerReward]);

  if (!open) return null;

  const num = (v) => Number(v);
  const rewardNum = num(pb);
  const budgetNum = num(bdg);
  const cntNum = num(cnt);

  const canInputBudget = (
    Number.isInteger(cntNum) && cntNum >= 1 && cntNum <= 4 &&
    Number.isFinite(rewardNum) && rewardNum > 0
  );

  const minBudget = canInputBudget 
    ? headcount * cntNum * rewardNum * 10 
    : 0;

  const validNums = () => {
    const a = rewardNum;
    const b = budgetNum;
    const c = cntNum;
    return (
      Number.isFinite(a) && a > 0 &&
      Number.isFinite(b) && b >= minBudget &&
      Number.isInteger(c) && c >= 1 && c <= 4
    );
  };

  const save = async (overrideUse) => {
    const finalUse = typeof overrideUse === 'boolean' ? overrideUse : use;
    if (!projectId) { 
      setAlertMsg('먼저 1단계를 저장해주세요.'); 
      return; 
    }
    if (finalUse) {
      if (!validNums()) { 
        setAlertMsg(`입력값을 확인해주세요.\n예산은 최소 ${minBudget.toLocaleString()}포인트 이상이어야 합니다.`); 
        return; 
      }
      if (!agree) { 
        setAlertMsg('확인 체크가 필요합니다.'); 
        return; 
      }
    }

    setSaving(true);
    try {
      const body = finalUse
        ? { use: true, rewardPerBuyer: rewardNum, budgetTotal: budgetNum, settlementCount: cntNum }
        : { use: false };
      const res = await fetch(`${API_URL}/api/marketer/mybiz/projects/${encodeURIComponent(projectId)}/purchase-reward`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j?.ok) throw new Error(j?.message || '저장 실패');

      onSaved({
        use: finalUse,
        rewardPerBuyer: finalUse ? rewardNum : '',
        budgetTotal: finalUse ? budgetNum : '',
        settlementCount: finalUse ? cntNum : ''
      });
      onClose();
    } catch (e) { 
      setAlertMsg(e.message || '저장 실패'); 
    }
    setSaving(false);
  };

  const handleNext = () => {
    if (!validNums()) {
      setAlertMsg(`입력값을 확인해주세요.\n예산은 최소 ${minBudget.toLocaleString()}포인트 이상이어야 합니다.`);
      return;
    }
    setStep(2);
  };

  return (
    <>
      <div className="per-reward-modal mbm-overlay">
        <div className="per-reward-modal__content mbm">
          <button onClick={onClose} aria-label="close" className="per-reward-modal__close mbm-close">×</button>

          {/* Step 0: 초기 선택 */}
          {step === 0 && (
            <div className="per-reward-modal__body">
              <div className="per-reward-modal__header">
                <div className="per-reward-modal__icon">💰</div>
                <div className="per-reward-modal__title mbm-title">CPS 리워드를 추가로 제공하시겠습니까?</div>
                <div className="per-reward-modal__description">
                  인플루언서가 유입한 구매 고객 수에 따라 성과형으로 지급하는 리워드입니다.
                </div>
              </div>

              <div className="per-reward-modal__info-box">
                <div className="per-reward-modal__info-title">⚠️ 유의사항</div>
                <div className="per-reward-modal__info-text">
                  인플루언서 별로 성과를 확인하기 위해, 광고로 유입된 구매량을 식별할 수 있는 시스템을 가지고 계셔야 합니다.<br/>
                  (ex. 네이버 스마트 스토어(nt파라미터), 자사몰의 경우 유입 경로를 파악할 수 있는 URL link)
                </div>
              </div>

              <div className="per-reward-modal__actions mbm-foot">
                <button
                  type="button"
                  onClick={() => save(false)}
                  className="per-reward-modal__button per-reward-modal__button--no mb-btn mb-btn-secondary"
                  disabled={saving}
                >
                  제공하지 않음
                </button>
                <button
                  type="button"
                  onClick={() => { setUse(true); setStep(1); }}
                  className="per-reward-modal__button per-reward-modal__button--yes mb-btn mb-btn-primary"
                >
                  제공하기
                </button>
              </div>
            </div>
          )}

          {/* Step 1: 리워드 설정 */}
          {step === 1 && (
            <div className="per-reward-modal__body">
              <div className="per-reward-modal__header">
                <div className="per-reward-modal__title mbm-title">CPS 리워드 상세 설정</div>
              </div>

              <div className="per-reward-modal__info-badge">
                <span className="per-reward-modal__info-badge-label">모집 인원</span>
                <span className="per-reward-modal__info-badge-value">{headcount}명</span>
              </div>

              <div className="per-reward-modal__form">
                <div className="per-reward-modal__field">
                  <label className="per-reward-modal__label">
                    📊 결산 횟수
                  </label>
                  <div className="per-reward-modal__input-wrapper">
                    <input 
                      type="text"
                      inputMode="numeric"
                      placeholder="1~4" 
                      value={cnt} 
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setCnt(value);
                      }}
                      className="per-reward-modal__input" 
                    />
                    <span className="per-reward-modal__input-suffix">회</span>
                  </div>
                  <div className="per-reward-modal__hint">
                    💡 최대 4회까지 설정 가능합니다
                  </div>
                </div>

                <div className="per-reward-modal__field">
                  <label className="per-reward-modal__label">
                    💵 유입 구매 고객당 리워드
                  </label>
                  <div className="per-reward-modal__input-wrapper">
                    <input 
                      type="text"
                      inputMode="numeric"
                      placeholder="예: 5000" 
                      value={pb} 
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setPb(value);
                      }}
                      className="per-reward-modal__input" 
                    />
                    <span className="per-reward-modal__input-suffix">포인트</span>
                  </div>
                  <div className="per-reward-modal__hint">
                    💡 구매 1건당 지급할 리워드 포인트
                  </div>
                </div>

                <div className="per-reward-modal__field">
                  <label className="per-reward-modal__label">
                    💳 CPS 보증금 (CPS 리워드 예산)
                  </label>
                  <div className="per-reward-modal__input-wrapper">
                    <input 
                      type="text"
                      inputMode="numeric"
                      placeholder={canInputBudget ? `최소 ${minBudget.toLocaleString()}` : '위 항목을 먼저 입력해주세요'} 
                      value={bdg} 
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setBdg(value);
                      }}
                      className="per-reward-modal__input" 
                      disabled={!canInputBudget}
                    />
                    <span className="per-reward-modal__input-suffix">포인트</span>
                  </div>
                  <div className="per-reward-modal__hint">
                    💡 최소 보증금은 모집인원 × 결산횟수 × 구매당리워드 × 10 으로 계산되며, 인플루언서가 유입한 구매 고객이 많아 CPS예산(보증금)이 초과되어도, 설정하신 예산 이내에서만 CPS 리워드를 지급하게 됩니다.
                    {canInputBudget && (
                      <div className="per-reward-modal__calculation">
                        ({headcount}명 × {cntNum}회 × {rewardNum.toLocaleString()}pt × 10 = 최소 {minBudget.toLocaleString()}pt)
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="per-reward-modal__actions mbm-foot">
                <button
                  type="button"
                  onClick={() => setStep(0)}
                  className="per-reward-modal__button per-reward-modal__button--back mb-btn mb-btn-secondary"
                >
                  이전
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="per-reward-modal__button per-reward-modal__button--next mb-btn mb-btn-primary"
                >
                  다음
                </button>
              </div>
            </div>
          )}

          {/* Step 2: 최종 확인 */}
          {step === 2 && (
            <div className="per-reward-modal__body">
              <div className="per-reward-modal__header">
                <div className="per-reward-modal__icon">✅</div>
                <div className="per-reward-modal__title mbm-title">최종 확인</div>
              </div>

              <div className="per-reward-modal__confirm-box">
                <div className="per-reward-modal__confirm-title">⚠️ 진행 전 확인사항</div>
                <div className="per-reward-modal__confirm-text">
                  • 각 인플루언서별 유입 고객을 식별할 수 있는 URL link를 선정 시 입력해야 합니다.<br/>
                  • 결산일마다 인플루언서 별 유입 수를 입력해야 합니다. (결산일은 프로젝트 일정에서 설정)
                </div>
              </div>

              <div className="per-reward-modal__summary-box">
                <div className="per-reward-modal__summary-title">설정 내용</div>
                <div className="per-reward-modal__summary-items">
                  <div className="per-reward-modal__summary-item">
                    <span className="per-reward-modal__summary-label">구매당 리워드</span>
                    <span className="per-reward-modal__summary-value">{rewardNum.toLocaleString()} 포인트</span>
                  </div>
                  <div className="per-reward-modal__summary-item">
                    <span className="per-reward-modal__summary-label">총 예산</span>
                    <span className="per-reward-modal__summary-value">{budgetNum.toLocaleString()} 포인트</span>
                  </div>
                  <div className="per-reward-modal__summary-item">
                    <span className="per-reward-modal__summary-label">결산 횟수</span>
                    <span className="per-reward-modal__summary-value">{cntNum}회</span>
                  </div>
                </div>
              </div>

              <label className="per-reward-modal__checkbox">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                <span>위 내용을 모두 확인했습니다</span>
              </label>

              <div className="per-reward-modal__actions mbm-foot">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="per-reward-modal__button per-reward-modal__button--back mb-btn mb-btn-secondary"
                  disabled={saving}
                >
                  이전
                </button>
                <button
                  type="button"
                  onClick={() => save()}
                  disabled={saving || !agree}
                  className="per-reward-modal__button per-reward-modal__button--submit mb-btn mb-btn-primary"
                >
                  {saving ? '저장 중...' : '설정 완료'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <AlertModal 
        open={!!alertMsg} 
        onClose={() => setAlertMsg('')} 
        message={alertMsg} 
      />
    </>
  );
}
